import { Field } from 'formik';
import React, { useCallback } from 'react';
import { Accordion, Card, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FormikMaterialUiTimePicker, Icon, FormikCheckBox } from '../../../components';
import { daysOfWeek } from '../FormOptions';
import useTimeCalculation from '../../../../hooks/useTimeCalculation';

const WeekDistribution = ({
  currentTab,
  dayCheckBoxValue,
  disabled,
  errors,
  getIn,
  onChangeTab,
  setFieldValue,
  touched,
  values
}) => {
  const { agreedSchedule, flexibleShift, shiftDaysAttributes } = values.shift;
  const { advanceSettings } = useSelector(state => state.utils);
  const nightShiftRange = advanceSettings
    .filter(asItem => asItem.code === 'night_shift_range')
    .some(x => x.textValue === 'true');
  const { calculateEndTime, calculateLunchDuration } = useTimeCalculation();
  const memoizedCalculateEndTime = useCallback(calculateEndTime, []);
  const memoizedCalculateLunchDuration = useCallback(calculateLunchDuration, []);

  // Recalcula endTime del día cuando cambia startLunch o endLunch (solo para turno flexible + horario pactado)
  const handleLunchTimeChange = useCallback(
    (index, field, time) => {
      setFieldValue(field.name, time);
      if (flexibleShift && values.shift.initialTime && values.shift.workHours) {
        const isStartLunch = field.name.includes('startLunch');
        const currentStartLunch = shiftDaysAttributes[index].startLunch || '14:00';
        const currentEndLunch = shiftDaysAttributes[index].endLunch || '15:00';
        const newStartLunch = isStartLunch ? time : currentStartLunch;
        const newEndLunch = isStartLunch ? currentEndLunch : time;
        const lunchMinutes = String(memoizedCalculateLunchDuration(newStartLunch, newEndLunch));
        const endTime = memoizedCalculateEndTime(
          values.shift.initialTime,
          values.shift.workHours,
          lunchMinutes,
          values.shift.imputableColation
        );
        setFieldValue(`shift[shiftDaysAttributes][${index}][endTime]`, endTime);
      }
    },
    [
      flexibleShift,
      values.shift,
      shiftDaysAttributes,
      memoizedCalculateEndTime,
      memoizedCalculateLunchDuration,
      setFieldValue
    ]
  );
  return (
    <Card className="overflow-visible">
      <Col className={`${currentTab === 'distribution' ? 'top-header-green' : 'top-header-light-gray'}`}>
        <Accordion.Toggle
          as={Col}
          eventKey="4"
          className="card-header-title-dt"
          onClick={() => onChangeTab('distribution')}
        >
          Distribución
        </Accordion.Toggle>
      </Col>
      <Accordion.Collapse eventKey="4">
        <Card.Body className="div-content">
          {!disabled && <p>Haz click en un día para activarlo o desactivarlo en este turno semanal.</p>}
          {agreedSchedule ? (
            <Row className="justify-content-center">
              {daysOfWeek.map((item, index) => (
                <Col
                  xs={5}
                  sm={2}
                  lg
                  key={item.value}
                  className={`shift-card-container${shiftDaysAttributes[index].send === true ? '' : '-disabled'}`}
                >
                  <Row onClick={e => disabled || dayCheckBoxValue(item.value, index, e)}>
                    <Col md={12}>
                      {shiftDaysAttributes[index].send && (
                        <Icon
                          className="text-primary"
                          style={{ marginTop: '5px', position: 'absolute', left: '5px' }}
                          width={18}
                          icon="checkmark-circle"
                        />
                      )}
                      <p className="text-uppercase shift-card-title">{item.label}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="align-items-center" md={12}>
                      <p className="text-uppercase shift-card-label">Desde</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][startTime]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="09:00"
                            onChange={time => setFieldValue(field.name, time)}
                            disabled={!shiftDaysAttributes[index].send || flexibleShift || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={12}>
                      <p className="text-uppercase shift-card-label">Hasta</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][endTime]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="18:00"
                            onChange={time => setFieldValue(field.name, time)}
                            disabled={!shiftDaysAttributes[index].send || flexibleShift || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <p className="text-uppercase shift-card-title">Colación</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="align-items-center" md={12}>
                      <p className="text-uppercase shift-card-label">Desde</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][startLunch]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="14:00"
                            onChange={time => handleLunchTimeChange(index, field, time)}
                            disabled={!shiftDaysAttributes[index].send || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={12}>
                      <p className="text-uppercase shift-card-label">Hasta</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][endLunch]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="15:00"
                            onChange={time => handleLunchTimeChange(index, field, time)}
                            disabled={!shiftDaysAttributes[index].send || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className="pl-4" onClick={e => e.stopPropagation()}>
                      <Field name={`shift[shiftDaysAttributes][${index}][telecommuting]`}>
                        {({ field }) => (
                          <FormikCheckBox
                            {...field}
                            field={field}
                            label="Teletrabajo"
                            disabled={!shiftDaysAttributes[index].send || disabled}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  {nightShiftRange && (
                    <Row>
                      <Col className="mb-3" md={12}>
                        <Field name={`shift[shiftDaysAttributes][${index}][isNightShift]`}>
                          {({ field }) => (
                            <FormikCheckBox
                              {...field}
                              custom
                              field={field}
                              label="Turno nocturno (Resumen de turno)"
                              disabled={!shiftDaysAttributes[index].send || disabled}
                              onClick={() => {
                                setFieldValue(field.name, field.value === 'true' || field.value === true);
                              }}
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>
                  )}
                </Col>
              ))}
            </Row>
          ) : (
            <Row className="justify-content-center">
              {daysOfWeek.map((item, index) => (
                <Col
                  xs={5}
                  sm={2}
                  lg
                  key={item.value}
                  className={`shift-card-container${shiftDaysAttributes[index].send === true ? '' : '-disabled'}`}
                >
                  <Row onClick={e => disabled || dayCheckBoxValue(item.value, index, e)}>
                    <Col md={12}>
                      {shiftDaysAttributes[index].send && (
                        <Icon
                          className="text-primary"
                          style={{ marginTop: '5px', position: 'absolute', left: '5px' }}
                          width={18}
                          icon="checkmark-circle"
                        />
                      )}
                      <p className="text-uppercase shift-card-title">{item.label}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="mb-3 align-items-center" md={12}>
                      <p className="text-uppercase shift-card-label">Desde</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][startTime]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="09:00"
                            onChange={time => setFieldValue(field.name, time)}
                            disabled={!shiftDaysAttributes[index].send || flexibleShift || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="mb-3" md={12}>
                      <p className="text-uppercase shift-card-label">Hasta</p>
                      <Field name={`shift[shiftDaysAttributes][${index}][endTime]`}>
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            abbr
                            defaultValue="18:00"
                            onChange={time => setFieldValue(field.name, time)}
                            disabled={!shiftDaysAttributes[index].send || flexibleShift || disabled}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className="pl-4" onClick={e => e.stopPropagation()}>
                      <Field name={`shift[shiftDaysAttributes][${index}][telecommuting]`}>
                        {({ field }) => (
                          <FormikCheckBox
                            {...field}
                            field={field}
                            label="Teletrabajo"
                            disabled={!shiftDaysAttributes[index].send || disabled}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  {nightShiftRange && (
                    <Row>
                      <Col className="mb-3" md={12}>
                        <Field name={`shift[shiftDaysAttributes][${index}][isNightShift]`}>
                          {({ field }) => (
                            <FormikCheckBox
                              {...field}
                              custom
                              field={field}
                              label="Turno nocturno (Resumen de turno)"
                              disabled={!shiftDaysAttributes[index].send || flexibleShift || disabled}
                              onClick={() => {
                                setFieldValue(field.name, field.value === 'true' || field.value === true);
                              }}
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>
                  )}
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
};
export default WeekDistribution;
